import { plainToClass } from 'class-transformer';
import { Injectable } from '@angular/core';

import { CollectApiServiceBase } from './collect-api-base.service';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { ProgressIndicatorService } from './progress-indicator.service';
import { SimplifiedTreeNodePermissions } from '../models/site-metadata/simplified-tree-node-permissions.model';
import { SimplifiedPermissionRequest } from '../models/site-metadata/simplified-permission-request.model';
import { FolderPermissionService } from './folder-permission.service';
import { TestServiceMethodsData, CallServiceMethod } from './call-service-method.model';
import { FolderPermissionExpression } from '../models/site-metadata/folder-permission-expression.model';
import { FolderRolePermissionExpression } from '../models/site-metadata/folder-role-permission-expression.model';
import { FolderRoleMember } from '../models/site-metadata/folder-role-member.model';
import { AppInjector } from '../../app.module';
import { CurrentSiteService } from './current-site.service';
import { Role } from '../models/role.model';
import { FormInstanceService } from './form-instance.service';
import { FormInstance } from '../models/site-content/form-instance.model';
import { WorkflowTransition } from '../models/workflow-transition.model';
  
@Injectable({
    providedIn: 'root'
})
export class PermissionTestingService extends CollectApiServiceBase<SimplifiedTreeNodePermissions> {
    public constructor(http: HttpClient, progressIndicatorService: ProgressIndicatorService, private folderPermissionService: FolderPermissionService, private formInstanceService: FormInstanceService) {
        super(http, progressIndicatorService, environment.apiUrl, 'permissionsTesting', SimplifiedTreeNodePermissions)
    }

    public formatResponse(data: SimplifiedTreeNodePermissions): SimplifiedTreeNodePermissions {
        let obj = plainToClass(SimplifiedTreeNodePermissions, data);
        return (obj);
    }

    // Test service method.
    public testPermissionsUsingObjects(objectIds: number[]): void {
        // Require more than one folder Id.
        if (objectIds.length < 3)
            throw `PermissionTestingService.testPermissionsUsingObjects():  at least a site Id and two folder Ids are required to test this service.`;

        // Define test-related constants.
        const testGroupMemberType = 'Group';
        const testGroupMember = 'AGY-BRD';
        const testGroupMemberId = 'AGY-BRD';
        const testUserMemberType = 'User';
        const testUserMember = 'TestUsr';
        const testUserMemberId = 'TestUsrId';

        // Define a hash to contain the result of prior calls, indexed by method name.
        //let resultOfPriorCalls = {};
        let testServiceMethodsData: TestServiceMethodsData = new TestServiceMethodsData();
        let dataCollectionId: number = objectIds[0];
        let folderId1: number = objectIds[1];
        let folderId2: number = objectIds[2];
        let formInstanceId: number = objectIds.length > 3 ? objectIds[3] : 0;

        let contributorRole = 'Contributor';
        let builderRole = 'Builder';
        let reviewerRole = 'Reviewer';
        let contributorPermissions: object =
        {
            'canViewFolder': true,
            'canCreateFolder': false,
            'canCreateForm': true,
            'canDeleteForm': true,
            'canGrantPermission': false,
            'canManageRoleMembers': true,

            'workflowPermissions': {
                'Start': {
                    'canViewFormInstance': true,
                    'canEditFormInstance': true
                },
                'End': {
                    'canViewFormInstance': true,
                    'canEditFormInstance': false
                }
            }
        };
        let builderPermissions: object =
        {
            'canViewFolder': true,
            'canCreateFolder': true,
            'canCreateForm': false,
            'canDeleteForm': false,
            'canGrantPermission': true,
            'canManageRoleMembers': false,

            'workflowPermissions': {
                'Start': {
                    'canViewFormInstance': true,
                    'canEditFormInstance': true
                },
                'End': {
                    'canViewFormInstance': true,
                    'canEditFormInstance': false
                }
            }
        };
        let reviewerPermissions: object =
        {
            'canViewFolder': true,
            'canCreateFolder': false,
            'canCreateForm': false,
            'canDeleteForm': false,
            'canGrantPermission': true,
            'canManageRoleMembers': false,

            'workflowPermissions': {
                'Start': {
                    'canViewFormInstance': true,
                    'canEditFormInstance': true
                },
                'End': {
                    'canViewFormInstance': true,
                    'canEditFormInstance': false
                }
            }
        };
        let contributorAndReviewerPermissions: object = 
        {
            'canViewFolder': true,
            'canCreateFolder': false,
            'canCreateForm': true,
            'canDeleteForm': true,
            'canGrantPermission': true,
            'canManageRoleMembers': true,

            'workflowPermissions': {
                'Start': {
                    'canViewFormInstance': true,
                    'canEditFormInstance': true
                },
                'End': {
                    'canViewFormInstance': false,
                    'canEditFormInstance': false
                }
            }
        };

        // Question:  what will the test calls below do?
        //
        // Answer:  the following calls will use a single folder to test assigning different permission expressions
        //          and configuring different role members.  These steps can be summarized as:
        //
        //              1. Setup:  make sure the built-in permission expressions and other expected environmental
        //                         data is set for the test site identified by one of the ids passed into this method;
        //              2. Create Custom Permission Expressions:  configure multiple permission expressions that
        //                 give different permissions to different roles;
        //              3. Assign one of the created permission expressions to a target folder;
        //              4. Assign one member to one of the roles in the assigned permission expression;
        //              5. Call the new 'GetFolderPermissions' PermissionsTestingController end point to get the
        //                 permissions associated with the target folder and the assigned role member;
        //              6. Confirm all of the permission flags returned from the call to GetFolderPermissions;
        //              7. Repeat steps 4-6 using multiple roles and multiple different role members;
        //              8. Repeat steps 3-7 using the other permission expressions created in step 2.
        //              9. Follow a comparable scenario to test the permissions associated with a form
        //                 instance that is contained within the target folder.

        // Define data that characterizes the methods to be tested.
        let folderPermissionTests: CallServiceMethod[] =
            [
                // 1. Setup
                new CallServiceMethod(
                    {
                        methodName: 'createStandardPermissionExpressions',
                        repeatMethodNumTimes: 0,
                        callExpr: (service: PermissionTestingService, testServiceMethodsData: TestServiceMethodsData) => {
                            let promise = service.folderPermissionService.createStandardPermissionExpressions(dataCollectionId);
                            return promise;
                        }
                    }
                ),
                new CallServiceMethod(
                    {
                        methodName: 'assignDefaultPermissionExpressionToAllFolders',
                        repeatMethodNumTimes: 0,
                        callExpr: (service: PermissionTestingService, testServiceMethodsData: TestServiceMethodsData) => {
                            let promise = service.folderPermissionService.assignDefaultPermissionExpressionToAllFolders(dataCollectionId);
                            return promise;
                        }
                    }
                ),

                // 2. Create Custom Permission Expressions
                new CallServiceMethod(
                    {
                        methodName: 'createFolderPermissionExpression',
                        repeatMethodNumTimes: 0,
                        callExpr: (service: PermissionTestingService, testServiceMethodsData: TestServiceMethodsData) => {
                            let folderPermissionExpression = new FolderPermissionExpression();
                            folderPermissionExpression.dataCollectionId = dataCollectionId;
                            folderPermissionExpression.name = 'Permission Expression 1';
                            folderPermissionExpression.tag = 'PE_1';
                            folderPermissionExpression.description = 'Permission Expression 1';
                            folderPermissionExpression.colorRedValue = 1;
                            folderPermissionExpression.colorGreenValue = 2;
                            folderPermissionExpression.colorBlueValue = 3;
                            folderPermissionExpression.colorAlphaValue = 4;
                            folderPermissionExpression.canBeSelectedForFolders = true;
                            folderPermissionExpression.isDefaultPermissionExpressionFlag = false;
                            folderPermissionExpression.isBuiltInPermissionExpressionFlag = false;
                            let promise = service.folderPermissionService.createFolderPermissionExpression(folderId2, folderPermissionExpression);
                            return promise;
                        }
                    }),
                new CallServiceMethod(
                    {
                        methodName: 'addFolderRolePermission1',
                        repeatMethodNumTimes: 0,
                        callExpr: (service: PermissionTestingService, testServiceMethodsData: TestServiceMethodsData) => {
                            let folderExpressionId: number = testServiceMethodsData.resultOfPriorCalls['createFolderPermissionExpression']['id'];
                            let currentSiteService = AppInjector.get(CurrentSiteService);
                            let role: Role = currentSiteService.Site.roles.find(r => r.name == contributorRole);
                            if (role == null)
                                throw `PermissionTestingService:  cannot find role '${contributorRole}' in site ${dataCollectionId}`;
                            let rolePermissionExpression: FolderRolePermissionExpression = new FolderRolePermissionExpression(folderExpressionId, role.id);
                            PermissionTestingService.assignPermissionsToRole(rolePermissionExpression, contributorPermissions);

                            let promise = service.folderPermissionService.addFolderRolePermission(rolePermissionExpression);
                            return promise;
                        }
                    }),
                new CallServiceMethod(
                    {
                        methodName: 'addFolderRolePermission2',
                        repeatMethodNumTimes: 0,
                        callExpr: (service: PermissionTestingService, testServiceMethodsData: TestServiceMethodsData) => {
                            let folderExpressionId: number = testServiceMethodsData.resultOfPriorCalls['createFolderPermissionExpression']['id'];
                            let currentSiteService = AppInjector.get(CurrentSiteService);
                            let role: Role = currentSiteService.Site.roles.find(r => r.name == builderRole);
                            if (role == null)
                                throw `PermissionTestingService:  cannot find role '${builderRole}' in site ${dataCollectionId}`;
                            let rolePermissionExpression: FolderRolePermissionExpression = new FolderRolePermissionExpression(folderExpressionId, role.id);
                            PermissionTestingService.assignPermissionsToRole(rolePermissionExpression, builderPermissions);

                            let promise = service.folderPermissionService.addFolderRolePermission(rolePermissionExpression);
                            return promise;
                        }
                    }),
                new CallServiceMethod(
                    {
                        methodName: 'addFolderRolePermission3',
                        repeatMethodNumTimes: 0,
                        callExpr: (service: PermissionTestingService, testServiceMethodsData: TestServiceMethodsData) => {
                            let promise = PermissionTestingService.addRolePermissionTo(testServiceMethodsData.resultOfPriorCalls['createFolderPermissionExpression'], dataCollectionId, reviewerRole, reviewerPermissions, service);
                            return promise;
                        }
                    }),
                // addRolePermissionTo(testServiceMethodsData: TestServiceMethodsData, dataCollectionId: number, roleName: string, rolePermissions: object, service: PermissionTestingService):

                // 3. Assign one of the created permission expressions to a target folder
                new CallServiceMethod(
                    {
                        methodName: 'assignFolderExpressionToFolder1',
                        repeatMethodNumTimes: 0,
                        callExpr: (service: PermissionTestingService, testServiceMethodsData: TestServiceMethodsData) => {
                            let folderExpressionId: number = testServiceMethodsData.resultOfPriorCalls['createFolderPermissionExpression']['id'];
                            let promise = service.folderPermissionService.assignFolderExpressionToFolder(folderExpressionId, folderId1, true, true);
                            return promise;
                        }
                    }),

                // 4. Assign one member to one of the roles in the assigned permission expression
                new CallServiceMethod(
                    {
                        methodName: 'addFolderRoleMember',
                        repeatMethodNumTimes: 0,
                        callExpr: (service: PermissionTestingService, testServiceMethodsData: TestServiceMethodsData) => {
                            let promise = PermissionTestingService.addRoleMember(testServiceMethodsData, 'addFolderRolePermission1', contributorRole, folderId1, testGroupMemberType, testGroupMember, testGroupMemberId, service);
                            return promise;
                        }
                    }),

                // 5. Call the new 'GetFolderPermissions' PermissionsTestingController end point to get the permissions associated with the target folder and the assigned role member
                new CallServiceMethod(
                    {
                        methodName: 'getFolderPermissions1',
                        repeatMethodNumTimes: 0,
                        callExpr: (service: PermissionTestingService, testServiceMethodsData: TestServiceMethodsData) => {
                            let request: SimplifiedPermissionRequest = new SimplifiedPermissionRequest(folderId1, testGroupMemberType, testGroupMember);
                            let promise = service.getFolderPermissions(request);
                            return promise;
                        }
                    }),

                // 6. Confirm all of the permission flags returned from the call to GetFolderPermissions;
                new CallServiceMethod(
                    {
                        methodName: 'validateExpectedFolderPermissions1',
                        repeatMethodNumTimes: 0,
                        callExpr: (service: PermissionTestingService, testServiceMethodsData: TestServiceMethodsData) => {
                            let returnedPermissions: SimplifiedTreeNodePermissions = testServiceMethodsData.resultOfPriorCalls['getFolderPermissions1'];

                            // Note:  this CallServiceMethod instance does not actually call a service method;
                            //        rather, it returns a Promise coded right here, as the test mechanism
                            //        expects each CallServiceMethod instances to return a promise from callExpr.
                            let promise = new Promise<SimplifiedTreeNodePermissions>((resolve, reject) => {
                                PermissionTestingService.performAndReportPermissionValidation(returnedPermissions, contributorPermissions, resolve, reject);
                            });

                            return promise;
                        }
                    }),

                // Repeat steps 4-6:
                // 4. Assign one member to one of the roles in the assigned permission expression
                new CallServiceMethod(
                    {
                        methodName: 'addFolderRoleMember',
                        repeatMethodNumTimes: 0,
                        callExpr: (service: PermissionTestingService, testServiceMethodsData: TestServiceMethodsData) => {
                            let promise = PermissionTestingService.addRoleMember(testServiceMethodsData, 'addFolderRolePermission2', builderRole, folderId1, testUserMemberType, testUserMember, testUserMemberId, service);
                            return promise;
                        }
                    }),

                // 5. Call the new 'GetFolderPermissions' PermissionsTestingController end point to get the permissions associated with the target folder and the assigned role member
                new CallServiceMethod(
                    {
                        methodName: 'getFolderPermissions2',
                        repeatMethodNumTimes: 0,
                        callExpr: (service: PermissionTestingService, testServiceMethodsData: TestServiceMethodsData) => {
                            let request: SimplifiedPermissionRequest = new SimplifiedPermissionRequest(folderId1, testUserMemberType, testUserMember);
                            let promise = service.getFolderPermissions(request);
                            return promise;
                        }
                    }),

                // 6. Confirm all of the permission flags returned from the call to GetFolderPermissions;
                new CallServiceMethod(
                    {
                        methodName: 'validateExpectedFolderPermissions2',
                        repeatMethodNumTimes: 0,
                        callExpr: (service: PermissionTestingService, testServiceMethodsData: TestServiceMethodsData) => {
                            let returnedPermissions: SimplifiedTreeNodePermissions = testServiceMethodsData.resultOfPriorCalls['getFolderPermissions2'];

                            // Note:  this CallServiceMethod instance does not actually call a service method;
                            //        rather, it returns a Promise coded right here, as the test mechanism
                            //        expects each CallServiceMethod instances to return a promise from callExpr.
                            let promise = new Promise<SimplifiedTreeNodePermissions>((resolve, reject) => {
                                PermissionTestingService.performAndReportPermissionValidation(returnedPermissions, builderPermissions, resolve, reject);
                            });

                            return promise;
                        }
                    }),

                // Repeat steps 4-6:
                // 4. Assign one member to one of the roles in the assigned permission expression
                new CallServiceMethod(
                    {
                        methodName: 'addFolderRoleMember',
                        repeatMethodNumTimes: 0,
                        callExpr: (service: PermissionTestingService, testServiceMethodsData: TestServiceMethodsData) => {
                            let promise = PermissionTestingService.addRoleMember(testServiceMethodsData, 'addFolderRolePermission3', reviewerRole, folderId1, testGroupMemberType, testGroupMember, testGroupMemberId, service);
                            return promise;
                        }
                    }),

                // 5. Call the new 'GetFolderPermissions' PermissionsTestingController end point to get the permissions associated with the target folder and the assigned role member
                new CallServiceMethod(
                    {
                        methodName: 'getFolderPermissions3',
                        repeatMethodNumTimes: 0,
                        callExpr: (service: PermissionTestingService, testServiceMethodsData: TestServiceMethodsData) => {
                            let request: SimplifiedPermissionRequest = new SimplifiedPermissionRequest(folderId1, testGroupMemberType, testGroupMember);
                            let promise = service.getFolderPermissions(request);
                            return promise;
                        }
                    }),

                // 6. Confirm all of the permission flags returned from the call to GetFolderPermissions;
                new CallServiceMethod(
                    {
                        methodName: 'validateExpectedFolderPermissions3',
                        repeatMethodNumTimes: 0,
                        callExpr: (service: PermissionTestingService, testServiceMethodsData: TestServiceMethodsData) => {
                            let returnedPermissions: SimplifiedTreeNodePermissions = testServiceMethodsData.resultOfPriorCalls['getFolderPermissions3'];

                            // Note:  this CallServiceMethod instance does not actually call a service method;
                            //        rather, it returns a Promise coded right here, as the test mechanism
                            //        expects each CallServiceMethod instances to return a promise from callExpr.
                            let promise = new Promise<SimplifiedTreeNodePermissions>((resolve, reject) => {
                                PermissionTestingService.performAndReportPermissionValidation(returnedPermissions, contributorAndReviewerPermissions, resolve, reject);
                            });

                            return promise;
                        }
                    }),

                    // Not yet coded steps:
                    //              7. Repeat steps 4-6 using multiple roles and multiple different role members;
                    //              8. Repeat steps 3-7 using the other permission expressions created in step 2.
                    //              9. Follow a comparable scenario to test the permissions associated with a form
            ];

        let folderAndFormInstancePermissionTests: CallServiceMethod[] = null;

        if (formInstanceId > 0) {
            let formInstanceTests: CallServiceMethod[] =
                [
                    // 9a. Get the form instance.
                    new CallServiceMethod(
                        {
                            methodName: 'getFormInstance1',
                            repeatMethodNumTimes: 0,
                            callExpr: (service: PermissionTestingService, testServiceMethodsData: TestServiceMethodsData) => {
                                let promise = service.formInstanceService.getContent(formInstanceId);
                                return promise;
                            }
                        }
                    ),

                    // 9b. Set the form instance's workflow state to 'Start'.
                    new CallServiceMethod(
                        {
                            methodName: 'setStartWorkflowState',
                            repeatMethodNumTimes: 0,
                            callExpr: (service: PermissionTestingService, testServiceMethodsData: TestServiceMethodsData) => {
                                let formInstance: FormInstance = testServiceMethodsData.resultOfPriorCalls['getFormInstance1'];

                                let promise = PermissionTestingService.setFormInstanceWorkflowState(formInstance, 'Start', service.formInstanceService);
                                return promise;
                            }
                        }
                    ),
                    new CallServiceMethod(
                        {
                            methodName: 'getFormInstanceAfterSettingStartState',
                            repeatMethodNumTimes: 0,
                            callExpr: (service: PermissionTestingService, testServiceMethodsData: TestServiceMethodsData) => {
                                let promise = service.formInstanceService.getContent(formInstanceId);
                                return promise;
                            }
                        }
                    ),

                    // 9c. Query the form instance's permissions.
                    new CallServiceMethod(
                        {
                            methodName: 'getStartPermissions',
                            repeatMethodNumTimes: 0,
                            callExpr: (service: PermissionTestingService, testServiceMethodsData: TestServiceMethodsData) => {
                                let formInstance: FormInstance = testServiceMethodsData.resultOfPriorCalls['getFormInstanceAfterSettingStartState'];
                                if (formInstance.currentWorkflowState != 'Start')
                                    throw `PermissionsTestingService:  when trying to validate workflow state permissions, the form instances is not in the expected 'Start' worflow state.`;

                                let request: SimplifiedPermissionRequest = new SimplifiedPermissionRequest(formInstanceId, testGroupMemberType, testGroupMember);
                                let promise = service.getFormInstancePermissions(request);
                                return promise;
                            }
                        }),

                    // 9d. Confirm all of the permission flags returned from the call to GetFolderPermissions;
                    new CallServiceMethod(
                        {
                            methodName: 'validateExpectedFormInstanceStartPermissions',
                            repeatMethodNumTimes: 0,
                            callExpr: (service: PermissionTestingService, testServiceMethodsData: TestServiceMethodsData) => {
                                let returnedPermissions: SimplifiedTreeNodePermissions = testServiceMethodsData.resultOfPriorCalls['getStartPermissions'];

                                // Note:  this CallServiceMethod instance does not actually call a service method;
                                //        rather, it returns a Promise coded right here, as the test mechanism
                                //        expects each CallServiceMethod instances to return a promise from callExpr.
                                let promise = new Promise<SimplifiedTreeNodePermissions>((resolve, reject) => {
                                    PermissionTestingService.performAndReportPermissionValidation(returnedPermissions, contributorAndReviewerPermissions, resolve, reject, 'Start');
                                });

                                return promise;
                            }
                        }),

                    // Repeat 9a-d:
                    // 9a. Get the form instance.
                    new CallServiceMethod(
                        {
                            methodName: 'getFormInstance2',
                            repeatMethodNumTimes: 0,
                            callExpr: (service: PermissionTestingService, testServiceMethodsData: TestServiceMethodsData) => {
                                let promise = service.formInstanceService.getContent(formInstanceId);
                                return promise;
                            }
                        }
                    ),

                    // 9b. Set the form instance's workflow state to 'End'.
                    new CallServiceMethod(
                        {
                            methodName: 'setEndWorkflowState',
                            repeatMethodNumTimes: 0,
                            callExpr: (service: PermissionTestingService, testServiceMethodsData: TestServiceMethodsData) => {
                                let formInstance: FormInstance = testServiceMethodsData.resultOfPriorCalls['getFormInstance2'];

                                let promise = PermissionTestingService.setFormInstanceWorkflowState(formInstance, 'End', service.formInstanceService);
                                return promise;
                            }
                        }
                    ),
                    new CallServiceMethod(
                        {
                            methodName: 'getFormInstanceAfterSettingEndState',
                            repeatMethodNumTimes: 0,
                            callExpr: (service: PermissionTestingService, testServiceMethodsData: TestServiceMethodsData) => {
                                let promise = service.formInstanceService.getContent(formInstanceId);
                                return promise;
                            }
                        }
                    ),

                    // 9c. Query the form instance's permissions.
                    new CallServiceMethod(
                        {
                            methodName: 'getEndPermissions',
                            repeatMethodNumTimes: 0,
                            callExpr: (service: PermissionTestingService, testServiceMethodsData: TestServiceMethodsData) => {
                                let formInstance: FormInstance = testServiceMethodsData.resultOfPriorCalls['getFormInstanceAfterSettingEndState'];
                                if (formInstance.currentWorkflowState != 'End')
                                    throw `PermissionsTestingService:  when trying to validate workflow state permissions, the form instances is not in the expected 'End' worflow state.`;

                                let request: SimplifiedPermissionRequest = new SimplifiedPermissionRequest(formInstanceId, testGroupMemberType, testGroupMember);
                                let promise = service.getFormInstancePermissions(request);
                                return promise;
                            }
                        }),

                    // 9d. Confirm all of the permission flags returned from the call to GetFolderPermissions;
                    new CallServiceMethod(
                        {
                            methodName: 'validateExpectedFormInstanceEndPermissions',
                            repeatMethodNumTimes: 0,
                            callExpr: (service: PermissionTestingService, testServiceMethodsData: TestServiceMethodsData) => {
                                let returnedPermissions: SimplifiedTreeNodePermissions = testServiceMethodsData.resultOfPriorCalls['getEndPermissions'];

                                // Note:  this CallServiceMethod instance does not actually call a service method;
                                //        rather, it returns a Promise coded right here, as the test mechanism
                                //        expects each CallServiceMethod instances to return a promise from callExpr.
                                let promise = new Promise<SimplifiedTreeNodePermissions>((resolve, reject) => {
                                    PermissionTestingService.performAndReportPermissionValidation(returnedPermissions, contributorAndReviewerPermissions, resolve, reject, 'End');
                                });

                                return promise;
                            }
                        }),

                ];

            folderAndFormInstancePermissionTests = folderPermissionTests.concat(formInstanceTests);
        }

        // Call the method that will test using the array of CallServiceMethod instances defined just above.
        if (folderAndFormInstancePermissionTests != null)
            CallServiceMethod.testServiceMethods('PermissionTestingService', this, folderAndFormInstancePermissionTests, testServiceMethodsData);
        else
            CallServiceMethod.testServiceMethods('PermissionTestingService', this, folderPermissionTests, testServiceMethodsData);
    }

    private static addRolePermissionTo(folderPermissionExpression: FolderPermissionExpression, dataCollectionId: number, roleName: string, rolePermissions: object, service: PermissionTestingService): Promise<FolderPermissionExpression> {
        if (folderPermissionExpression == null)
            throw `PermissionTestingService.addRolePermissionTo():  the provided folder permission expression is null.`;
        let currentSiteService = AppInjector.get(CurrentSiteService);
        let role: Role = currentSiteService.Site.roles.find(r => r.name == roleName);
        if (role == null)
            throw `PermissionTestingService:  cannot find role '${roleName}' in site ${dataCollectionId}`;
        let rolePermissionExpression: FolderRolePermissionExpression = new FolderRolePermissionExpression(folderPermissionExpression.id, role.id);
        PermissionTestingService.assignPermissionsToRole(rolePermissionExpression, rolePermissions);

        let promise = service.folderPermissionService.addFolderRolePermission(rolePermissionExpression);
        return promise;
    }
    private static assignPermissionsToRole(rolePermissionExpression: FolderRolePermissionExpression, mapOfPermissions: object): void {
        for (let permissionPropertyName in mapOfPermissions)
            rolePermissionExpression[permissionPropertyName] = mapOfPermissions[permissionPropertyName]
    }
    private static addRoleMember(testServiceMethodsData: TestServiceMethodsData, priorCallName: string, roleName, folderId: number, memberType, member, memberId, service: PermissionTestingService): Promise<FolderRolePermissionExpression> {
        let folderPermissionExpression: FolderPermissionExpression = testServiceMethodsData.resultOfPriorCalls[priorCallName];
        if ((folderPermissionExpression == null) || (folderPermissionExpression.folderRolePermissionExpressions == null) || (folderPermissionExpression.folderRolePermissionExpressions.length == 0))
            throw `PermissionTestingService.testServiceUsingObject():  could not get the recently added folder role permission expression for role '${roleName}'`;
        let folderRolePermissionExpression: FolderRolePermissionExpression = folderPermissionExpression.folderRolePermissionExpressions.find(rpe => rpe.roleName == roleName);
        let folderRoleMember: FolderRoleMember = new FolderRoleMember(folderId, folderRolePermissionExpression.id, memberType, member, memberId);
        let promise = service.folderPermissionService.addFolderRoleMember(folderRoleMember);
        return promise;
    }
    private static validateExpectedPermissions(mapOfExpectedPermissions: object, actualPermissions: SimplifiedTreeNodePermissions, workflowState: string = null): boolean {
        let validated: boolean = true;

        if (workflowState == null) {
            // Since we have no specified workflow state, validate folder permissions.
            for (let permissionPropertyName in mapOfExpectedPermissions) {
                if (permissionPropertyName == 'workflowPermissions')
                    continue; // Skip this part of the data structure.

                if (actualPermissions[permissionPropertyName] != mapOfExpectedPermissions[permissionPropertyName]) {
                    validated = false;
                    break;
                }
            }
        } else {
            // We have a workflow state, so validate form instance permissions.
            let workflowPermissions: object = mapOfExpectedPermissions['workflowPermissions'];
            let workflowStatePermissions: object = workflowPermissions != null ? workflowPermissions[workflowState] : null;
            if (workflowStatePermissions == null)
                throw `validateExpectedPermissions():  could not find expected permissions for workflow state '${workflowState}'`;

            for (let permissionPropertyName in workflowStatePermissions) {
                if (actualPermissions[permissionPropertyName] != workflowStatePermissions[permissionPropertyName]) {
                    validated = false;
                    break;
                }
            }
        }

        return validated;
    }
    private static performAndReportPermissionValidation(returnedPermissions: SimplifiedTreeNodePermissions, expectedPermissions: object, resolve, reject, workflowState: string = null): void {
        let validated: boolean = PermissionTestingService.validateExpectedPermissions(expectedPermissions, returnedPermissions, workflowState);
        if (validated)
            resolve(returnedPermissions);
        else
            reject(returnedPermissions);
    }

    private static setFormInstanceWorkflowState(formInstance: FormInstance, workflowState: string, formInstanceService: FormInstanceService): Promise<FormInstance> {
        let promise: Promise<FormInstance> = null;
        if (formInstance.currentWorkflowState != workflowState) {
            //formInstance.currentWorkflowState = workflowState;
            let transition: WorkflowTransition = null;
            if (formInstance.workflowStateTransitions != null) {
                transition = formInstance.workflowStateTransitions.find(t => t.endStateName == workflowState);
                if ((transition == null) && (formInstance.workflowStateTransitions.length > 0))
                    transition = formInstance.workflowStateTransitions[0];
            }
            if (transition == null)
                throw `PermissionsTestingService.setFormInstanceWorkflowState():  cannot find a transition to workflow state '${workflowState}'.`;
            formInstance.selectedWorkflowTransitionEndStateId = transition.endStateId;

            promise = formInstanceService.transitionWorkflowState(formInstance);
        }
        else {
            promise = new Promise<FormInstance>((resolve, reject) => {
                resolve(formInstance);
            });
        }

        return promise;
    }

    // Service methods.
    public getFolderPermissions(request: SimplifiedPermissionRequest): Promise<SimplifiedTreeNodePermissions> {
        let url = `${this.url}/${this.endpoint}/getFolderPermissions`;
        return this.http.post<SimplifiedTreeNodePermissions>(url, request)
            .toPromise()
            .then(response => {
                let result: SimplifiedTreeNodePermissions = this.formatResponse(response);
                return result;
            });
    }

    public getFormInstancePermissions(request: SimplifiedPermissionRequest): Promise<SimplifiedTreeNodePermissions> {
        let url = `${this.url}/${this.endpoint}/getFormInstancePermissions`;
        return this.http.post<SimplifiedTreeNodePermissions>(url, request)
            .toPromise()
            .then(response => {
                let result: SimplifiedTreeNodePermissions = this.formatResponse(response);
                return result;
            });
    }
}
